import React, { useState, useMemo, useEffect, useCallback, useRef } from "react"
import cs from "classnames"
import CollapsibleCard from "../CollapsibleCard"

import useStore from "store"
import { getLoggerInstance } from "utils/logger"
import { selectRole } from "store/selectors/auth"
import { SUPERVISOR } from "constants/roles"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"

const TABS = {
  ACCESS_CODE: "Access Code",
  CALL_SHEET: "Call Sheet",
  CASE_NOTES: "Case Notes",
  SCRIPTS: "Scripts",
  REPORT_IT_ISSUE: "Report IT Issue",
  SUPERVISOR_NOTES: "Supervisor Notes",
}

const logger = getLoggerInstance()

const CallForm = ({
  className,
  saved,
  wizardStatus,
  requiresAccessCode,
  validAccessCode,
  refreshCallSheet,
  AccessCode,
  CallSheet,
  CaseNotes,
  SupervisorNotes,
  Scripts,
  ReportIT,
}) => {
  const supervisorNotesEnabled = useConfigFlag("ui.supervisorNotesEnabled")
  const [tab, openTab] = useState(TABS.CALL_SHEET)
  const role = useStore(selectRole)
  const env = useConfigFlag("env")
  const developerEnvironment = useMemo(() => !["prod", "qa"].includes(env), [env])
  
  const showTab = useCallback(
    (name) => {
      if (name === TABS.ACCESS_CODE && !requiresAccessCode) {
        return false
      }
      if (name === TABS.CALL_SHEET && !validAccessCode && !developerEnvironment) {
        return false
      }
      if (name === TABS.SUPERVISOR_NOTES && (role !== SUPERVISOR || !supervisorNotesEnabled)) {
        return false
      }
      return true
    },
    [requiresAccessCode, role, validAccessCode, supervisorNotesEnabled, developerEnvironment]
  )

  const tabs = useMemo(() => Object.values(TABS).filter(showTab), [showTab])

  const visKeyRef = useRef(null)
  const recoveryPendingRef = useRef(false)
  useEffect(() => {
    const accessTabShown = tabs.includes(TABS.ACCESS_CODE)
    const callSheetTabShown = tabs.includes(TABS.CALL_SHEET)
    const key = `${accessTabShown}:${callSheetTabShown}`
    if (visKeyRef.current === key) return
    visKeyRef.current = key

    const context = {
      tabs,
      accessTabShown,
      callSheetTabShown,
      requiresAccessCode,
      validAccessCode,
      developerEnvironment,
    }

    if (callSheetTabShown && recoveryPendingRef.current) {
      recoveryPendingRef.current = false
      logger.debug("Call Sheet tab now visible after prior Access-only tab visibility", context)
      return
    }

    if (accessTabShown && !callSheetTabShown) {
      recoveryPendingRef.current = true
      logger.debug("Access Code tab visible, Call Sheet tab hidden", context)
    }
  }, [tabs, requiresAccessCode, validAccessCode, developerEnvironment])

  const _openTab = useCallback(
    (name) => {
      if (name === TABS.CALL_SHEET) {
        refreshCallSheet()
      }
      openTab(name)
    },
    [refreshCallSheet]
  )

  useEffect(() => {
    if (validAccessCode) {
      openTab((previousTab) => {
        if (previousTab === TABS.ACCESS_CODE) {
          logger.debug("Switching to CALL_SHEET tab due to valid access code")
        }
        return TABS.CALL_SHEET
      })
    } else if (requiresAccessCode && !validAccessCode) {
      logger.debug("Displaying ACCESS_CODE tab due to access code validation required")
      openTab(TABS.ACCESS_CODE)
    }
  }, [requiresAccessCode, validAccessCode])

  return (
    <CollapsibleCard
      title="Call Form"
      minSize="21rem"
      disable={wizardStatus !== "complete"}
      saved={saved}
      className={className}
      onSelectTab={_openTab}
      selectedTab={tab}
      tabs={tabs}
    >
      <AccessCode className={cs({ hidden: tab !== TABS.ACCESS_CODE }, "w-full")} />
      <CallSheet className={cs({ hidden: tab !== TABS.CALL_SHEET }, "w-full")} />
      <CaseNotes className={cs({ hidden: tab !== TABS.CASE_NOTES }, "w-full")} />
      <SupervisorNotes className={cs({ hidden: tab !== TABS.SUPERVISOR_NOTES }, "w-full")} />
      <Scripts className={cs({ hidden: tab !== TABS.SCRIPTS }, "w-full")} />
      <ReportIT className={cs({ hidden: tab !== TABS.REPORT_IT_ISSUE }, "w-full")} />
    </CollapsibleCard>
  )
}

export default CallForm
